import { cpfValido } from '@zenda/kernel/cpf';
import { formatBR, parseBR, type PhoneParseError } from '@zenda/kernel/phone';

const digitos = (v: string): string => v.replace(/\D+/g, '');

/**
 * Telefone brasileiro enquanto se digita: `(11) 98765-4321`, `(11) 3456-7890`.
 *
 * Quem começa com `+` e outro país fica como digitou: a máscara de lá não é a nossa.
 * O `+55` e o `0` de discagem saem, porque o DDD já diz o resto.
 */
export function mascararTelefone(v: string): string {
  const cru = v.trim();
  if (cru.startsWith('+') && !digitos(cru).startsWith('55')) return cru;
  let d = digitos(cru);
  if (cru.startsWith('+') || (d.startsWith('55') && d.length > 11)) d = d.slice(2);
  if (d.startsWith('0')) d = d.slice(1);
  d = d.slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  const corte = resto.length > 8 ? 5 : 4;
  return resto.length <= corte
    ? `(${ddd}) ${resto}`
    : `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`;
}

const ERRO_DE_TELEFONE: Record<PhoneParseError, string> = {
  EMPTY: 'Informe o telefone com DDD.',
  NOT_NUMERIC: 'O telefone só pode ter números.',
  BAD_LENGTH: 'Faltam números: informe DDD e número, como (11) 98765-4321.',
  UNKNOWN_DDD: 'Esse DDD não existe. Confira os dois primeiros números.',
  BAD_SUBSCRIBER: 'Número inválido: celular começa com 9 e fixo com 2 a 5.',
  NOT_BR: 'Só números do Brasil por enquanto.',
};

/** Nulo quando o telefone é válido; senão, o que corrigir. */
export function erroDoTelefone(v: string): string | null {
  const r = parseBR(v);
  return r.ok ? null : ERRO_DE_TELEFONE[r.error];
}

/** A forma em que o número será guardado e casado com o WhatsApp, para mostrar. */
export function telefoneCanonico(v: string): string | null {
  const r = parseBR(v);
  return r.ok ? formatBR(r.value) : null;
}

/** `+5511987654321`, a chave com que o número é guardado. */
export function telefoneE164(v: string): string | null {
  const r = parseBR(v);
  return r.ok ? r.value.e164 : null;
}

export function mascararCpf(v: string): string {
  const d = digitos(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function erroDoCpf(v: string): string | null {
  if (digitos(v) === '') return null;
  return cpfValido(v) === null ? 'CPF inválido. Confira os 11 números.' : null;
}

export function mascararCep(v: string): string {
  const d = digitos(v).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
}

export function erroDoCep(v: string): string | null {
  const d = digitos(v);
  return d === '' || d.length === 8 ? null : 'CEP tem 8 números, como 01310-100.';
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function erroDoEmail(v: string): string | null {
  const s = v.trim();
  return s === '' || EMAIL.test(s) ? null : 'E-mail inválido. Ex.: nome@clinica.com.br';
}

/**
 * Valor em reais enquanto se digita, com milhar e duas casas: `1.234,56`.
 *
 * Os centavos entram pela direita, como em caixa eletrônico: digitar 1, 2, 3, 4, 5, 6
 * dá 1.234,56. Assim não existe vírgula ou ponto ambíguo para interpretar.
 */
export function mascararReais(v: string): string {
  const d = digitos(v).replace(/^0+/, '').slice(0, 11);
  if (d === '') return '';
  const centavos = d.padStart(3, '0');
  const inteiro = centavos.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${inteiro},${centavos.slice(-2)}`;
}

export function reaisParaNumero(v: string): number | null {
  const d = digitos(v);
  return d === '' ? null : Number(d) / 100;
}

export function numeroParaReais(n: number | null | undefined): string {
  return n == null ? '' : mascararReais(Math.round(n * 100).toString());
}

/** Só dígitos, até `max`: dia do mês, duração, quantidade. */
export function soNumeros(v: string, max: number): string {
  return digitos(v).slice(0, max);
}
